import { useMutation, useQueryClient } from "react-query";
import { mutatePutFn, mutatePostFn } from "../../services/networking.service";
import ENDPOINTS from "../../services/endPoints";

/**
 * Phase 0 non-destructive subscription levers (NOT the old immediate deleteKey
 * cancel). See docs/domain/owner-return-and-key-retirement.md.
 *
 * cancelSubscription:
 *   - normal case → schedule cancel-at-period-end (cancelled_by "admin"), open
 *     the owner return code + grace deadline.
 *   - if the key has subscriptionCancelFailedAt set → this is the resolve path
 *     for a retirement whose Stripe cancel errored: it retries the immediate
 *     Stripe cancel and clears the flag. Key status is untouched.
 *
 * reactivateSubscription: only while still inside the paid period
 * (cancelAtPeriodEnd true) — clears the cancellation and revokes the return code.
 */

export const useAdminCancelSubscription = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { keyId: string }>(
    ({ keyId }) => mutatePutFn(ENDPOINTS.KEY_CANCEL_SUBSCRIPTION, { keyId }),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.GET_KEY_DETAILS]);
      },
    }
  );
};

export const useAdminReactivateSubscription = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { keyId: string }>(
    ({ keyId }) => mutatePutFn(ENDPOINTS.KEY_REACTIVATE_SUBSCRIPTION, { keyId }),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.GET_KEY_DETAILS]);
      },
    }
  );
};

/** POST /admin/key/comp — N free days via Stripe trial_end (survives the
 *  6-hourly subscription re-sync). Forces billing_status active. */
export const useAdminCompKey = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { key_id: string; days: number; reason?: string }>(
    (payload) => mutatePostFn(ENDPOINTS.KEY_COMP, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.GET_KEY_DETAILS]);
      },
    }
  );
};

/** POST /admin/key/payOutstanding — pay a lapsed key's open invoice on the
 *  owner's behalf, on the card already on file. */
export const useAdminPayOutstanding = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { key_id: string }>(
    (payload) => mutatePostFn(ENDPOINTS.KEY_PAY_OUTSTANDING, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.GET_KEY_DETAILS]);
      },
    }
  );
};
